/**
 * Is the tool still quoting current government figures?
 *
 * `ratesFreshness.test.ts` already fails once `RATES_REVIEW_BY` passes, but that alarm only rings where
 * a DEVELOPER can hear it. If nobody runs the tests for a few weeks the app keeps using the old rates
 * with nothing on screen to say so, and an adviser producing a Statement of Advice on 25 September
 * would quote a superseded Age Pension figure without ever being told.
 *
 * This is the same date read at RUN time, so the person giving the advice sees it too. One source, one
 * date to move: update `RATES_REVIEW_BY` when the tables are refreshed and both the test and this notice
 * go quiet together.
 */
import { RATES_REVIEW_BY, RATES_PROVENANCE } from './constants/ratesAndThresholds.ts';
// The next figures are usually verified and staged ahead of the day in ratesAndThresholds.ts (see
// `RATES_FROM_1_OCTOBER_2026`); the review on the day is to apply them, not to find them.

export interface RatesReviewStatus {
    /** True once the scheduled indexation date has passed without the tables being refreshed. */
    overdue: boolean;
    /** The date the figures were next due to move, ISO `YYYY-MM-DD`. */
    reviewBy: string;
    /** Whole days since that date; 0 or negative while still current. */
    daysOverdue: number;
    /** Ready-to-show sentence, or null while the figures are current. */
    notice: string | null;
}

/** Local midnight, so the comparison never trips a day early on a timezone east of Greenwich. */
function localDate(iso: string): Date {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year!, month! - 1, day!);
}

/**
 * Whole days between two LOCAL calendar dates, counted on the calendar rather than in milliseconds.
 *
 * Subtracting timestamps is wrong across a daylight-saving boundary: Australia moves its clocks in
 * early October, so 180 days after a 20 September review date is an hour short of 180 × 86,400,000 and
 * floors to 179. Harmless in a day count, not harmless in the `overdue` test it also drives — that
 * would have shown the warning a day late, or withheld it a day early, twice a year. Reading the
 * calendar components through `Date.UTC` removes the offset entirely.
 */
function wholeDaysBetween(from: Date, to: Date): number {
    const asUtcDay = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.round((asUtcDay(to) - asUtcDay(from)) / 86_400_000);
}

/** `20 September 2026` — the form a reader recognises from a Centrelink letter. */
export function formatReviewDate(iso: string): string {
    return localDate(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Whether the rate tables are past their review date, and the sentence to show if they are.
 *
 * `today` is injectable so callers can test it; it is otherwise the wall clock, deliberately — this
 * question is about the moment the advice is being given, not about the projection's own dates.
 */
export function ratesReviewStatus(today: Date = new Date()): RatesReviewStatus {
    const reviewBy = RATES_REVIEW_BY;
    const due = localDate(reviewBy);
    const daysOverdue = wholeDaysBetween(due, today);
    if (daysOverdue <= 0) {
        return { overdue: false, reviewBy, daysOverdue, notice: null };
    }
    return {
        overdue: true,
        reviewBy,
        daysOverdue,
        notice:
            `Government rates were due to be indexed on ${formatReviewDate(reviewBy)}, and these figures have not been ` +
            `updated since. The Age Pension, aged care and tax amounts shown may be out of date — check them against the ` +
            `current published rates before relying on them. (Age Pension and aged care are indexed 20 March and ` +
            `20 September; tax thresholds on 1 July; the MPIR quarterly.)`,
    };
}

/** What each figure was last checked against, for a reader who wants the detail rather than the warning. */
export { RATES_PROVENANCE };
